import { useState } from "react";
import { Link } from "react-router";
import { fmtInt } from "@/components/admin/format";
import { KIND_META, MASTER_KINDS, type MasterKind } from "@/components/admin/master-kinds";
import { LoadState, PageHead } from "@/components/admin/PageHead";
import { useLoad } from "@/components/admin/useLoad";
import { errorMessage } from "@/components/auth/issues";
import { Button } from "@/components/ui/FormControls";
import { InfoTip } from "@/components/ui/InfoTip";
import { api } from "@/lib/api";
import { useMaster } from "@/store/master";

interface Stats {
  users: number;
  usersWeek: number;
  marketing: number;
  scenarios: number;
  topOrganizations: { organization: string; count: number }[];
  masterOrigin: Partial<Record<MasterKind, "db" | "default">>;
}

type SeedResult = Record<string, { inserted: number; updated: number; skipped: number }>;

/** 운영 현황 , 가입,동의,시나리오 수, 기관별 참여, 마스터 데이터 출처와 기본값 채우기 */
export function DashboardPage() {
  const { data, error, loading, reload } = useLoad<Stats>("/admin/stats");
  const loadMaster = useMaster((s) => s.load);
  const [busy, setBusy] = useState<"seed" | "reset" | null>(null);
  const [note, setNote] = useState<{ kind: "ok" | "danger"; text: string } | null>(null);

  async function seed(overwrite: boolean) {
    if (overwrite && !window.confirm("DB 에 있는 모든 마스터 데이터 항목을 내장 기본값으로 덮어씁니다. 관리자가 고친 값이 사라집니다. 계속할까요?")) return;
    setNote(null);
    setBusy(overwrite ? "reset" : "seed");
    try {
      const r = await api<{ result: SeedResult }>(`/admin/master/seed${overwrite ? "?overwrite=1" : ""}`, { method: "POST" });
      const sum = Object.values(r.result).reduce((a, s) => ({ inserted: a.inserted + s.inserted, updated: a.updated + s.updated, skipped: a.skipped + s.skipped }), { inserted: 0, updated: 0, skipped: 0 });
      setNote({ kind: "ok", text: `${overwrite ? "되돌리기" : "채우기"} 완료. 추가 ${sum.inserted}건, 덮어씀 ${sum.updated}건, 그대로 ${sum.skipped}건` });
      reload();
      void loadMaster();
    } catch (e) {
      setNote({ kind: "danger", text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="admin-page">
      <PageHead title="현황" desc="가입자와 저장된 시나리오, 마스터 데이터가 어디서 오는지 한눈에." />
      <LoadState loading={loading && !data} error={error} />
      {data && (
        <>
          <section className="stat-grid" aria-label="요약">
            <Stat label="가입자" value={data.users} />
            <Stat label="최근 7일 가입" value={data.usersWeek} />
            <Stat label="마케팅 동의" value={data.marketing} sub={data.users ? `${Math.round((data.marketing / data.users) * 100)}%` : undefined} />
            <Stat label="저장된 시나리오" value={data.scenarios} />
          </section>

          <div className="grid gap-section lg:grid-cols-2">
            <section className="admin-card">
              <div><h2 className="admin-card-title">기관별 가입자</h2>
              <p className="admin-card-desc">가입 시 적은 회사 또는 기관명 기준, 상위 10곳.</p></div>
              {data.topOrganizations.length === 0 ? (
                <p className="text-caption text-ink-subtle">아직 가입자가 없습니다.</p>
              ) : (
                <div className="table-scroll admin-table-wrap">
                  <table className="admin-table admin-table-compact">
                    <thead>
                      <tr>
                        <th>회사 또는 기관</th>
                        <th className="cell-num">인원</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topOrganizations.map((o) => (
                        <tr key={o.organization}>
                          <td>{o.organization || <span className="cell-muted">(미입력)</span>}</td>
                          <td className="cell-num">{fmtInt(o.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-footnote text-ink-subtle">
                명단과 연락처는 <Link to="/admin/users" className="text-accent underline">가입자</Link>에서 봅니다.
              </p>
            </section>

            <section className="admin-card">
              <div className="flex items-start gap-label">
                <div className="min-w-0 flex-1">
                  <div><h2 className="admin-card-title">마스터 데이터 출처</h2>
                  <p className="admin-card-desc">종류별로 DB 값을 쓰는지, 프로그램에 내장된 기본값을 쓰는지.</p></div>
                </div>
                <InfoTip label="출처 규칙">DB 에 그 종류의 항목이 하나라도 있으면 DB 를, 없으면 내장 기본값을 씁니다. '기본값 채우기' 는 DB 에 없는 키만 추가하고, '되돌리기' 는 같은 키의 DB 값을 기본값으로 덮어씁니다.</InfoTip>
              </div>
              <ul className="flex flex-col gap-label">
                {MASTER_KINDS.map((k) => {
                  const origin = data.masterOrigin[k];
                  return (
                    <li key={k} className="flex items-center justify-between gap-item text-caption">
                      <Link to={`/admin/master/${k}`} className="text-ink hover:text-accent hover:underline">{KIND_META[k].label}</Link>
                      {origin === "db" ? <span className="badge-accent">DB</span> : <span className="badge-muted">기본값</span>}
                    </li>
                  );
                })}
              </ul>
              <div className="form-actions">
                <Button variant="soft" disabled={busy !== null} onClick={() => void seed(false)}>
                  {busy === "seed" ? "채우는 중…" : "기본값 채우기"}
                </Button>
                <Button variant="danger" disabled={busy !== null} onClick={() => void seed(true)}>
                  {busy === "reset" ? "되돌리는 중…" : "기본값으로 되돌리기(덮어쓰기)"}
                </Button>
              </div>
              {note && (
                <p role={note.kind === "danger" ? "alert" : "status"} className={`note note-${note.kind} text-label`}>
                  {note.text}
                </p>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="admin-stat-card">
      <p className="stat-label">{label}</p>
      <p className="stat-value">
        {fmtInt(value)}
        {sub && <span className="ml-label text-caption font-semibold text-ink-muted">{sub}</span>}
      </p>
    </div>
  );
}
